/**
 * InterviewWritePage.tsx
 * 면담 기록 작성 페이지
 * 수정 모드 지원 (이어서 작성, 수정)
 *
 * URL:
 * - 신규: /org-management/interviews/:employeeId/new
 * - 수정: /org-management/interviews/:employeeId/new?interviewId=xxx
 *
 * Query params: date, time, location, type, interviewId (수정 시)
 *
 * 기능:
 * - 면담 일시/장소/타입 표시 (읽기전용)
 * - 면담자(조직장) 정보 표시 + 서명
 * - 대상자 정보 표시 (서명 비활성)
 * - 면담 내용 작성 (RichTextEditor)
 * - 저장 / 취소 버튼
 * - 임시저장 기능 제거 (저장 + 전송 2단계 UX로 단순화)
 * - 수정 모드 시 기존 데이터 로드 및 updateInterview 호출
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, Edit3, Send } from 'lucide-react';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { useEmployee } from '../../shared/hooks/useEmployees';
import {
  useInterviewMutations,
  useInterviewDetail,
  getSignatureUrl,
  deleteInterview,
  saveAndSendInterview,
  InterviewType,
  DeliveryStatus,
  DELIVERY_STATUS_LABELS,
} from '../../shared/hooks/useInterviews';
import { SignatureModal } from './components/SignatureModal';
import { RichTextEditor } from './components/RichTextEditor';
import { useSendInterviewToHr } from '../../shared/hooks/useHrInterviewShares';
import { getSignatureUrl as getStorageSignatureUrl } from '../../shared/utils/storage';
import { fetchOrgSelectedTemplateContent, fetchDefaultTemplateContent, useInterviewTypes } from '../../shared/hooks/useInterviewTypesManagement';
import { useAuthContext } from '../../shared/hooks/useAuthContext';

// 기본 면담 내용 템플릿 (HTML 형식)
// 리치 텍스트 에디터용 HTML 템플릿
const DEFAULT_CONTENT = `<p><strong>1. 면담 주요 내용(요약)</strong></p>
<p><br></p>
<p><br></p>
<p><br></p>
<p><strong>2. 상호 합의 및 향후 계획(핵심)</strong></p>
<p><br></p>`;

// HTML에서 텍스트만 추출하는 헬퍼
function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// 기본 템플릿 텍스트 (비교용)
const DEFAULT_TEXT_STRIPPED = '1. 면담 주요 내용(요약)2. 상호 합의 및 향후 계획(핵심)';

// V62: 내용이 실제로 입력되었는지 확인
// - 현재 로드된 템플릿 원본과 동일하면 미입력 취급
// - 기본 템플릿 텍스트와 동일해도 미입력 취급
// - 빈값/공백만이면 미입력 취급
// - 그 외 1글자 이상이면 OK
function hasActualContent(html: string, initialTemplateHtml?: string): boolean {
  const stripped = stripHtml(html).replace(/\s+/g, '');
  if (stripped.length === 0) return false; // 빈값/공백만
  // 기본 템플릿 그대로
  const defaultStripped = DEFAULT_TEXT_STRIPPED.replace(/\s+/g, '');
  if (stripped === defaultStripped) return false;
  // 로드된 템플릿(대표/조직/전역) 그대로
  if (initialTemplateHtml) {
    const initialStripped = stripHtml(initialTemplateHtml).replace(/\s+/g, '');
    if (initialStripped.length > 0 && stripped === initialStripped) return false;
  }
  return true; // 실제 내용 있음
}

export function InterviewWritePage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 수정 모드 확인
  const interviewId = searchParams.get('interviewId') || null;
  const isEditMode = !!interviewId;

  // URL에서 파라미터 추출 (신규 모드에서만 사용)
  const urlDate = searchParams.get('date') || '';
  const urlTime = searchParams.get('time') || '';
  const urlLocation = searchParams.get('location') || '';
  const urlType = (searchParams.get('type') || 'regular') as InterviewType;

  // 현재 사용자 (면담자)
  const { user } = useAuthorization();
  const authContext = useAuthContext();
  const userOrgUnitId = authContext.orgUnitId;
  const currentUserId = user?.profile?.id;

  // 면담자(현재 사용자) Employee 정보 조회
  const { employee: interviewerEmployee, isLoading: interviewerLoading } = useEmployee(currentUserId);

  // 대상자 정보 조회
  const { employee: targetEmployee, isLoading: targetLoading } = useEmployee(employeeId);

  // 기존 면담 데이터 조회 (수정 모드)
  const { interview: existingInterview, isLoading: interviewLoading } = useInterviewDetail(interviewId);

  // 로딩 상태
  const employeesLoading = interviewerLoading || targetLoading || (isEditMode && interviewLoading);

  // 면담 저장 mutations
  const { createInterview, updateInterview, isSubmitting } = useInterviewMutations();

  // 폼 상태
  const [content, setContent] = useState(DEFAULT_CONTENT);
  // V62: 최초 로드된 템플릿 원본 보관 (전송 시 미입력 판정 기준)
  const initialTemplateRef = useRef<string>(DEFAULT_CONTENT);
  const [interviewerSignature, setInterviewerSignature] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  // 대상자 서명 상태 (agreed 상태일 때 표시용)
  const [targetSignatureUrl, setTargetSignatureUrl] = useState<string | null>(null);
  const [targetSignatureLoading, setTargetSignatureLoading] = useState(false);
  const [targetSignatureError, setTargetSignatureError] = useState<{ code: string; message: string } | null>(null);

  // 수정 모드에서 사용할 면담 정보 (기존 데이터 또는 URL 파라미터)
  const interviewDate = isEditMode && existingInterview ? existingInterview.interviewDate : urlDate;
  const interviewTime = isEditMode && existingInterview ? existingInterview.interviewTime : urlTime;
  const interviewLocation = isEditMode && existingInterview ? existingInterview.location : urlLocation;
  const interviewType = isEditMode && existingInterview ? existingInterview.type : urlType;

  // 확인 모달 상태
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // 임시저장 모달 제거 (draft 기능 제거)

  // 삭제/전송 상태
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // V55.64: HR 전송 상태
  const [showHrSendConfirm, setShowHrSendConfirm] = useState(false);
  // V55.66: 재전송 확인 팝업 상태
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const { sendToHr, isSending: isSendingToHr } = useSendInterviewToHr();

  // 기존 면담 상태 파악
  // draft 기능 제거 - 모든 면담은 saved 상태
  const existingStatus = existingInterview?.status || 'saved';
  const existingDeliveryStatus = (existingInterview?.deliveryStatus || 'notSent') as DeliveryStatus;
  const isAgreed = existingDeliveryStatus === 'agreed';
  const isDisagreed = existingDeliveryStatus === 'disagreed';
  const isSent = existingDeliveryStatus === 'sent';
  const isNotSent = existingDeliveryStatus === 'notSent';
  const isSavedStatus = existingStatus === 'saved';

  // 읽기전용 모드: agreed(상호합의) 또는 sent(전송됨) 상태는 수정 불가
  // - agreed: 상호합의 완료 → 완전 읽기 전용
  // - sent: 대상자 확인 대기 중 → 면담자가 더 이상 수정 불가
  // - disagreed: 미동의 → 수정 가능 (재전송 필요)
  // - notSent: 미전송 → 수정 가능
  const isReadOnly = isEditMode && (isAgreed || isSent);

  // 수정 모드에서 기존 데이터로 폼 초기화
  useEffect(() => {
    if (isEditMode && existingInterview && !formInitialized) {
      const editContent = existingInterview.contentHtml || DEFAULT_CONTENT;
      setContent(editContent);
      initialTemplateRef.current = editContent; // V62: 수정 모드 원본 보관

      // 서명 이미지 로드
      // disagreed 상태: 재전송 시 새 서명 필수 → EXISTING 마커 설정 안함
      const needsResigning = existingInterview.deliveryStatus === 'disagreed';
      if (existingInterview.interviewerSignaturePath && !needsResigning) {
        getSignatureUrl(existingInterview.interviewerSignaturePath).then((url) => {
          if (url) {
            setInterviewerSignature('EXISTING'); // 마커로 사용
          }
        });
      }

      setFormInitialized(true);
    }
  }, [isEditMode, existingInterview, formInitialized]);

  // V268: 신규 면담 작성 시 조직별 대표 템플릿 자동 로드
  // 1순위: orgInterviewTemplates (조직별 대표) → 2순위: interviewTypes.defaultTemplateId (전역 fallback)
  useEffect(() => {
    if (isEditMode || formInitialized) return;
    if (!interviewType) return;

    let alive = true;
    (async () => {
      // 1순위: 조직별 대표 템플릿
      if (userOrgUnitId) {
        const orgResult = await fetchOrgSelectedTemplateContent(userOrgUnitId, interviewType);
        if (!alive) return;
        if (orgResult && orgResult.contentHtml) {
          setContent(orgResult.contentHtml);
          initialTemplateRef.current = orgResult.contentHtml; // V62: 대표 템플릿 원본 보관
          return;
        }
      }
      // 2순위: 전역 대표 템플릿 (fallback)
      const globalResult = await fetchDefaultTemplateContent({ typeCode: interviewType });
      if (!alive) return;
      if (globalResult && globalResult.contentHtml) {
        setContent(globalResult.contentHtml);
        initialTemplateRef.current = globalResult.contentHtml; // V62: 전역 템플릿 원본 보관
      }
    })();

    return () => { alive = false; };
  }, [isEditMode, formInitialized, interviewType, userOrgUnitId]);

  // 대상자 서명 로드 (agreed 상태일 때)
  useEffect(() => {
    if (!existingInterview || existingInterview.deliveryStatus !== 'agreed') {
      setTargetSignatureUrl(null);
      setTargetSignatureError(null);
      return;
    }

    const targetPath = existingInterview.targetSignaturePath;
    if (!targetPath) {
      setTargetSignatureError({ code: 'no-path', message: '서명 경로 없음' });
      return;
    }

    let alive = true;
    setTargetSignatureLoading(true);
    setTargetSignatureError(null);

    (async () => {
      const url = await getStorageSignatureUrl(targetPath);
      if (!alive) return;

      setTargetSignatureLoading(false);

      if (url) {
        setTargetSignatureUrl(url);
      } else {
        console.warn('[InterviewWritePage] 대상자 서명 URL 로드 실패:', targetPath);
        setTargetSignatureError({ code: 'url-fail', message: 'URL 로딩 실패' });
      }
    })();

    return () => { alive = false; };
  }, [existingInterview?.id, existingInterview?.deliveryStatus, existingInterview?.targetSignaturePath]);

  // 면담 일시 포맷팅
  const formattedDateTime = useMemo(() => {
    if (!interviewDate) return '-';
    const dateStr = interviewDate.replace(/-/g, '.');
    const timeStr = interviewTime || '00:00';
    return `${dateStr} ${timeStr}`;
  }, [interviewDate, interviewTime]);

  // V62: 면담 타입 라벨 — DB interviewTypes.name(SSoT) 전용, "면담 목적 = 면담 타입명" 정책
  // 하드코딩 INTERVIEW_TYPE_LABELS 사용 금지 — DB name만 참조
  const { types: activeInterviewTypes, isLoading: typesLoading } = useInterviewTypes();
  const typeLabel = useMemo(() => {
    // 1. DB interviewTypes 컬렉션의 name (SSoT) — 유일한 정식 경로
    const dbName = activeInterviewTypes.find(t => t.code === interviewType)?.name;
    if (dbName) return dbName;
    // 2. 수정 모드: 문서 저장값 (BE resolveTypeLabel이 DB에서 가져온 스냅샷)
    if (isEditMode && existingInterview?.typeLabel) return existingInterview.typeLabel;
    // 3. DB 로딩 중이면 대기 표시
    if (typesLoading) return '...';
    // 4. DB 로딩 완료인데 미존재 → '-'
    return '-';
  }, [activeInterviewTypes, interviewType, isEditMode, existingInterview?.typeLabel, typesLoading]);

  // 서명 저장 핸들러
  const handleSignatureSave = (base64: string) => {
    setInterviewerSignature(base64);
  };

  // 취소 확인
  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    navigate('/org-management/interviews');
  };

  // 임시저장(handleDraft) 함수 제거 - draft 기능 폐지

  // 저장 (완료)
  // 면담 장소 필수 검증 추가
  // 서명 필수 검증 + 저장 후 자동 이동 개선
  // 수정 모드 지원
  const handleSave = async () => {
    if (!targetEmployee || !interviewerEmployee) {
      alert('필수 정보가 누락되었습니다.');
      return;
    }

    if (!interviewLocation.trim()) {
      alert('면담 장소를 입력해주세요.');
      return;
    }

    // 저장(미전송)은 contentHtml/signature 모두 optional
    // - 저장 버튼: 초안 저장 개념 (장소/일시/타입만 필수)
    // - 전송 버튼(handleSaveAndSend): contentHtml + 서명 필수

    try {
      if (isEditMode && interviewId) {
        // 수정 모드: updateInterview 호출
        await updateInterview({
          id: interviewId,
          contentHtml: content,
          status: 'saved',
          signatureBase64: interviewerSignature && interviewerSignature !== 'EXISTING' ? interviewerSignature : undefined,
          interviewDate,
          interviewTime,
          location: interviewLocation,
          type: interviewType,
        });
      } else {
        // 신규 모드: createInterview 호출
        await createInterview({
          targetUserId: targetEmployee.id,
          targetUserName: targetEmployee.nameKo || '',
          targetUserOrgUnitName: targetEmployee.orgUnitName,
          targetUserPositionTitle: targetEmployee.positionTitle,
          interviewDate,
          interviewTime,
          location: interviewLocation,
          type: interviewType,
          contentHtml: content,
          status: 'saved',
          signatureBase64: interviewerSignature || '',
        });
      }

      // 성공 시 즉시 목록으로 이동 (alert 제거)
      navigate('/org-management/interviews');
    } catch (err: any) {
      alert(err.message || '저장에 실패했습니다.');
    }
  };

  // 삭제 핸들러
  const handleDelete = async () => {
    if (!interviewId) return;

    setIsDeleting(true);
    try {
      await deleteInterview(interviewId);
      setShowDeleteConfirm(false);
      navigate('/org-management/interviews');
    } catch (err: any) {
      alert(err.message || '삭제에 실패했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  // 저장+전송 통합 핸들러
  // 누락 필드를 모아서 한번에 안내
  // "대상자에게 전송" 버튼 클릭 시 저장과 전송을 원자적으로 처리
  const handleSaveAndSend = async () => {
    // 전송 필수값 검증 - 누락 필드 수집
    const missingLabels: string[] = [];

    if (!targetEmployee) {
      missingLabels.push('대상자');
    }

    if (!interviewLocation.trim()) {
      missingLabels.push('면담 장소');
    }

    if (!hasActualContent(content, initialTemplateRef.current)) {
      missingLabels.push('면담 내용');
    }

    // 서명 검증: 전송에서는 항상 새 서명 필요
    const hasNewSignature = interviewerSignature && interviewerSignature !== 'EXISTING';
    if (!hasNewSignature) {
      missingLabels.push('면담자 서명');
    }

    // 누락 필드가 있으면 한번에 안내
    if (missingLabels.length > 0) {
      alert(`전송 불가: ${missingLabels.join(', ')}을(를) 입력해주세요.`);
      setShowSendConfirm(false);
      return;
    }

    setIsSending(true);
    try {
      await saveAndSendInterview({
        interviewId: interviewId || undefined,
        targetUserId: targetEmployee!.id,
        targetUserName: targetEmployee!.nameKo || '',
        targetUserOrgUnitName: targetEmployee!.orgUnitName,
        targetUserPositionTitle: targetEmployee!.positionTitle,
        interviewDate,
        interviewTime,
        location: interviewLocation,
        type: interviewType,
        contentHtml: content,
        signatureBase64: interviewerSignature!,
      });

      setShowSendConfirm(false);
      alert('면담이 저장되고 대상자에게 전송되었습니다.');
      navigate('/org-management/interviews');
    } catch (err: any) {
      // BE에서 missingFields를 반환하면 우선 사용
      const details = err?.details;
      if (details?.missingLabels && Array.isArray(details.missingLabels)) {
        alert(`전송 불가: ${details.missingLabels.join(', ')}을(를) 입력해주세요.`);
      } else {
        alert(err.message || '저장+전송에 실패했습니다.');
      }
    } finally {
      setIsSending(false);
    }
  };

  // V55.64: HR에게 면담 전송 (V55.66: 재전송 확인 로직 추가)
  const handleSendToHr = async () => {
    if (!interviewId) return;

    try {
      // V55.66: 첫 호출 시 confirmResend 없이
      const result = await sendToHr(interviewId);

      // V55.66: 재전송 확인이 필요한 경우 (이미 전송 이력 있음)
      if (result.requireConfirm) {
        setShowHrSendConfirm(false);  // 첫 번째 팝업 닫기
        setShowResendConfirm(true);   // 재전송 확인 팝업 표시
        return; // 실제 전송 없이 종료
      }

      // 첫 전송 성공
      alert('인사팀에 성공적으로 전송되었습니다.');
      setShowHrSendConfirm(false);
    } catch (err: any) {
      alert(err.message || '인사팀 전송에 실패했습니다.');
    }
  };

  // V55.66: 재전송 확인 후 실제 전송
  const handleConfirmResend = async () => {
    if (!interviewId) return;

    try {
      // confirmResend=true로 재호출
      const result = await sendToHr(interviewId, true);

      if (result.shareId) {
        alert('인사팀에 성공적으로 전송되었습니다.');
      }
      setShowResendConfirm(false);
    } catch (err: any) {
      alert(err.message || '인사팀 전송에 실패했습니다.');
    }
  };

  // V55.66: 재전송 취소
  const handleCancelResend = () => {
    setShowResendConfirm(false);
  };

  // 로딩 중
  if (employeesLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            color: '#64748b',
          }}
        >
          <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '8px' }}>로딩 중...</span>
        </div>
      </div>
    );
  }

  // 대상자 없음
  if (!targetEmployee) {
    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: '16px',
          }}
        >
          <AlertCircle size={48} style={{ color: '#ef4444' }} />
          <p style={{ color: '#64748b' }}>대상 직원을 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate('/org-management/interviews')}
            style={{
              padding: '8px 16px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* 상단 네비게이션 */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => {
            // 읽기전용(agreed/sent)이면 confirm 없이 즉시 이동
            if (isReadOnly) {
              navigate('/org-management/interviews');
            } else {
              setShowCancelConfirm(true);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#64748b',
            fontSize: '14px',
          }}
        >
          <ArrowLeft size={18} />
          목록으로
        </button>
      </div>

      {/* 페이지 타이틀 - 수정 모드 표시, 상태 배지 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '32px',
          color: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <span>{targetEmployee.nameKo} {targetEmployee.positionTitle || ''} 면담 기록</span>
        {isEditMode && !isReadOnly && (
          <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 'normal' }}>
            (수정 중)
          </span>
        )}
        {isEditMode && isSavedStatus && (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 500,
              padding: '4px 10px',
              borderRadius: '4px',
              background: isAgreed ? '#dcfce7' : isDisagreed ? '#fee2e2' : isSent ? '#dbeafe' : '#f1f5f9',
              color: isAgreed ? '#22c55e' : isDisagreed ? '#ef4444' : isSent ? '#3b82f6' : '#64748b',
            }}
          >
            {DELIVERY_STATUS_LABELS[existingDeliveryStatus]}
          </span>
        )}
        {isReadOnly && (
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 'normal' }}>
            {isAgreed
              ? '(읽기 전용 - 상호합의 완료)'
              : isSent
              ? '(읽기 전용 - 대상자 확인 대기 중)'
              : '(읽기 전용)'}
          </span>
        )}
      </h1>

      {/* 면담 정보 섹션 */}
      <div
        style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#334155',
          }}
        >
          면담 정보
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {/* 면담 일시 */}
          <div>
            <label style={labelStyle}>면담 일시</label>
            <div style={readonlyValueStyle}>{formattedDateTime}</div>
          </div>

          {/* 면담 장소 */}
          <div>
            <label style={labelStyle}>면담 장소</label>
            <div style={readonlyValueStyle}>{interviewLocation || '-'}</div>
          </div>

          {/* 면담 목적 */}
          <div>
            <label style={labelStyle}>면담 목적</label>
            <div style={readonlyValueStyle}>{typeLabel}</div>
          </div>
        </div>
      </div>

      {/* 면담자 정보 섹션 */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#334155',
          }}
        >
          면담자 (관리자)
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {/* 소속 */}
          <div>
            <label style={labelStyle}>소속</label>
            <div style={readonlyValueStyle}>
              {isEditMode && existingInterview
                ? (existingInterview.interviewerOrgUnitName || '-')
                : (interviewerEmployee?.orgUnitName || '-')}
            </div>
          </div>

          {/* 성명 */}
          <div>
            <label style={labelStyle}>성명</label>
            <div style={readonlyValueStyle}>
              {isEditMode && existingInterview
                ? (existingInterview.interviewerName || '-')
                : (interviewerEmployee?.nameKo || '-')}
            </div>
          </div>

          {/* 서명 - 읽기전용이면 클릭 불가 */}
          <div>
            <label style={labelStyle}>서명</label>
            <div
              onClick={() => {
                // 읽기전용(agreed/sent)이면 서명 모달 열지 않음
                if (!isReadOnly) {
                  setShowSignatureModal(true);
                }
              }}
              style={{
                width: '120px',
                height: '50px',
                border: '1px dashed #cbd5e1',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isReadOnly ? 'default' : 'pointer',
                background: interviewerSignature ? '#ffffff' : '#f8fafc',
                overflow: 'hidden',
              }}
            >
              {interviewerSignature && interviewerSignature !== 'EXISTING' ? (
                <img
                  src={`data:image/png;base64,${interviewerSignature}`}
                  alt="서명"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              ) : interviewerSignature === 'EXISTING' ? (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#22c55e',
                    fontSize: '11px',
                  }}
                >
                  ✓ 서명 완료
                </span>
              ) : isDisagreed ? (
                // disagreed 상태: 기존 서명 있었지만 재전송 시 새 서명 필요
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#f59e0b',
                    fontSize: '11px',
                  }}
                >
                  <Edit3 size={14} />
                  재서명 필요
                </span>
              ) : (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#94a3b8',
                    fontSize: '12px',
                  }}
                >
                  {isReadOnly ? (
                    '서명 없음'
                  ) : (
                    <>
                      <Edit3 size={14} />
                      클릭하여 서명
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 대상자 정보 섹션 */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#334155',
          }}
        >
          면담 대상자
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {/* 소속 */}
          <div>
            <label style={labelStyle}>소속</label>
            <div style={readonlyValueStyle}>
              {targetEmployee.orgUnitName || '-'}
            </div>
          </div>

          {/* 성명 */}
          <div>
            <label style={labelStyle}>성명</label>
            <div style={readonlyValueStyle}>
              {targetEmployee.nameKo || '-'}
            </div>
          </div>

          {/* 대상자 서명 */}
          <div>
            <label style={labelStyle}>서명</label>
            <div
              style={{
                width: '120px',
                height: '50px',
                border: isAgreed ? '1px solid #22c55e' : '1px dashed #e2e8f0',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isAgreed ? '#ffffff' : '#f1f5f9',
                overflow: 'hidden',
              }}
            >
              {isAgreed ? (
                targetSignatureLoading ? (
                  <span style={{ color: '#f59e0b', fontSize: '11px' }}>로딩중...</span>
                ) : targetSignatureError ? (
                  <span style={{ color: '#ef4444', fontSize: '10px', textAlign: 'center', padding: '4px' }}>
                    {targetSignatureError.message}
                  </span>
                ) : targetSignatureUrl ? (
                  <img
                    src={targetSignatureUrl}
                    alt="대상자 서명"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    onError={() => {
                      setTargetSignatureError({ code: 'img-error', message: '이미지 로드 실패' });
                    }}
                  />
                ) : (
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>서명 없음</span>
                )
              ) : (
                <span style={{ color: '#94a3b8', fontSize: '11px' }}>(대상자 동의 전)</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 면담 내용 섹션 */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '16px',
            color: '#334155',
          }}
        >
          면담 내용
        </h2>

        {/* 리치 텍스트 에디터로 교체 */}
        {/* 읽기 전용 모드에서는 HTML 그대로 표시 */}
        {isReadOnly ? (
          <div
            style={{
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '8px',
              minHeight: '300px',
              lineHeight: '1.8',
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="면담 내용을 입력하세요..."
            minHeight="300px"
          />
        )}
      </div>

      {/* 미동의 사유 박스 (disagreed 상태에서만) */}
      {isEditMode && isDisagreed && existingInterview?.disagreementReason && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
          }}
        >
          <h2
            style={{
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '12px',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <AlertCircle size={16} />
            대상자 미동의 사유
          </h2>
          <div
            style={{
              padding: '12px 16px',
              background: '#fee2e2',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#991b1b',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
            }}
          >
            {existingInterview.disagreementReason}
          </div>
          <p
            style={{
              marginTop: '12px',
              fontSize: '13px',
              color: '#b91c1c',
            }}
          >
            * 내용을 수정하고 저장한 뒤, 다시 전송하세요.
          </p>
        </div>
      )}

      {/* 하단 버튼 - 상태별 버튼 표시 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        {/* 왼쪽: 삭제 버튼 (수정 모드 + 읽기전용 아닐 때 + disagreed 아닐 때) */}
        {/* disagreed 상태는 삭제 불가 (대상자가 피드백을 준 상태이므로 수정 후 재전송 필요) */}
        <div>
          {isEditMode && !isReadOnly && !isDisagreed && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting || isDeleting}
              style={{
                padding: '12px 24px',
                background: '#fee2e2',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc2626',
              }}
            >
              삭제
            </button>
          )}
        </div>

        {/* 오른쪽: 주요 버튼들 */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {/* 목록으로 버튼 (읽기전용 모드) */}
          {isReadOnly && (
            <button
              onClick={() => navigate('/org-management/interviews')}
              style={{
                padding: '12px 24px',
                background: '#f1f5f9',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#64748b',
              }}
            >
              목록으로
            </button>
          )}

          {/* V55.64: 인사팀에게 발송 버튼 (상호합의 상태에서만) */}
          {isReadOnly && isAgreed && (
            <button
              onClick={() => setShowHrSendConfirm(true)}
              disabled={isSendingToHr}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: '#059669',
                border: 'none',
                borderRadius: '8px',
                cursor: isSendingToHr ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: 'white',
                fontWeight: 500,
              }}
            >
              <Send size={16} />
              {isSendingToHr ? '전송 중...' : '인사팀에게 발송'}
            </button>
          )}

          {/* 신규 모드 또는 수정 모드 (읽기전용 아닐 때) */}
          {!isReadOnly && (
            <>
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={isSubmitting}
                style={{
                  padding: '12px 24px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#64748b',
                }}
              >
                취소
              </button>

              {/* 임시저장 버튼 제거 - draft 기능 폐지 */}

              {/* 저장 버튼 */}
              <button
                onClick={handleSave}
                disabled={isSubmitting}
                style={{
                  padding: '12px 24px',
                  background: '#4f46e5',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'white',
                  fontWeight: 500,
                }}
              >
                {isSubmitting ? '저장 중...' : '저장'}
              </button>

              {/* 전송 버튼 (저장+전송 통합)
                  - 신규 작성: 항상 표시
                  - 수정 모드: notSent 또는 disagreed일 때만 표시 (sent/agreed는 제외)
                  isDraftStatus 조건 제거 (draft 기능 폐지)
              */}
              {(!isEditMode || (isSavedStatus && (isNotSent || isDisagreed))) && (
                <button
                  onClick={() => setShowSendConfirm(true)}
                  disabled={isSubmitting || isSending}
                  style={{
                    padding: '12px 24px',
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'white',
                    fontWeight: 500,
                  }}
                >
                  {isSending ? '전송 중...' : '대상자에게 전송'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 서명 모달 */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
      />

      {/* 취소 확인 모달 */}
      {showCancelConfirm && (
        <ConfirmModal
          message="면담작성을 취소하시겠습니까? 작성 중인 내용은 저장되지 않습니다."
          onConfirm={handleCancelConfirm}
          onCancel={() => setShowCancelConfirm(false)}
        />
      )}

      {/* 임시저장 모달 제거 - draft 기능 폐지 */}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <ConfirmModal
          message="정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting}
          confirmLabel="삭제"
          confirmColor="#dc2626"
        />
      )}

      {/* 저장+전송 확인 모달 */}
      {showSendConfirm && (
        <ConfirmModal
          message="저장 후 대상자에게 전송하시겠습니까?"
          onConfirm={handleSaveAndSend}
          onCancel={() => setShowSendConfirm(false)}
          isLoading={isSending}
          confirmLabel="저장 후 전송"
          confirmColor="#3b82f6"
        />
      )}

      {/* V55.64: HR 전송 확인 모달 */}
      {showHrSendConfirm && (
        <ConfirmModal
          message="이 면담 기록을 인사팀에게 전송하시겠습니까?"
          onConfirm={handleSendToHr}
          onCancel={() => setShowHrSendConfirm(false)}
          isLoading={isSendingToHr}
          confirmLabel="인사팀에 전송"
          confirmColor="#059669"
        />
      )}

      {/* V55.66: 재전송 확인 모달 (이미 전송 이력 있을 때) */}
      {showResendConfirm && (
        <ConfirmModal
          message="이미 전송 이력이 있는 면담입니다. 다시 발송하시겠습니까?"
          onConfirm={handleConfirmResend}
          onCancel={handleCancelResend}
          isLoading={isSendingToHr}
          confirmLabel="확인"
          confirmColor="#059669"
        />
      )}
    </div>
  );
}

// ============================================
// 확인 모달 컴포넌트
// ============================================

interface ConfirmModalProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean; // 중복 클릭 방지
  confirmLabel?: string; // 확인 버튼 라벨
  confirmColor?: string; // 확인 버튼 색상
  cancelLabel?: string; // V55.66: 취소 버튼 라벨
}

// isLoading prop 추가로 저장 중 버튼 비활성화
// confirmLabel, confirmColor 추가
// V55.66: cancelLabel 추가
function ConfirmModal({
  message,
  onConfirm,
  onCancel,
  isLoading = false,
  confirmLabel = '예',
  confirmColor = '#4f46e5',
  cancelLabel = '아니오',
}: ConfirmModalProps) {
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
        }}
        onClick={onCancel}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
          zIndex: 1001,
          minWidth: '300px',
          textAlign: 'center',
        }}
      >
        <p style={{ marginBottom: '24px', color: '#334155', lineHeight: '1.6' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: '8px 20px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            아니오
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '8px 20px',
              background: confirmColor,
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: 'white',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? '처리 중...' : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================
// 스타일
// ============================================

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  marginBottom: '6px',
};

const readonlyValueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#1e293b',
  fontWeight: 500,
};
